// src/pages/invoices/components/BankDropdown.tsx
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getBankOptionLabel } from '@/lib/constants/bank-details';
import { cn } from '@/lib/utils';
import { BankDetails } from '@/types/bank-details.types';
import { Check, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

interface BankDropdownProps {
  bankDetails: BankDetails[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const BankDropdown = ({
  bankDetails,
  value,
  onChange,
  placeholder = 'Select bank account...',
}: BankDropdownProps) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const selectedBank = bankDetails.find((b) => b._id === value);

  const filteredBanks = bankDetails.filter((bank) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      bank.bankName.toLowerCase().includes(searchLower) ||
      bank.accountHolderName.toLowerCase().includes(searchLower) ||
      bank.accountNumber.includes(search)
    );
  });

  const handleSelect = (bank: BankDetails) => {
    onChange(bank._id);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type="button"
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm',
          'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'hover:bg-accent hover:text-accent-foreground'
        )}
      >
        {selectedBank ? (
          <span className="truncate text-left flex-1">
            {getBankOptionLabel(selectedBank.bankName)} - ****{selectedBank.accountNumber.slice(-4)}
          </span>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>

      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search bank..."
            autoFocus
          />
        </div>

        <div className="max-h-60 overflow-auto p-1">
          {filteredBanks.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">No bank found.</div>
          ) : (
            filteredBanks.map((bank) => {
              return (
                <div
                  key={bank._id}
                  onClick={() => handleSelect(bank)}
                  className={cn(
                    'flex cursor-pointer items-center rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground',
                    value === bank._id && 'bg-accent'
                  )}
                >
                  <Check
                    className={cn('mr-2 h-4 w-4', value === bank._id ? 'opacity-100' : 'opacity-0')}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{getBankOptionLabel(bank.bankName)}</span>
                    <span className="text-xs text-muted-foreground">
                      {bank.accountHolderName} - ****{bank.accountNumber.slice(-4)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default BankDropdown;
